'use client';

import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { useToast } from '@/contexts/ToastContext';
import type { useTranslations } from 'next-intl';
import { fetchWithAuth } from '@/lib/utils/api-auth';
import { extractApiErrorMessage } from '@/hooks/dashboard/useDashboardProjects';
import type { Project as ProjectSummary } from '@/types/project';
import type { ProjectFolder } from '@/components/home/theme-editor';

interface UseDashboardFolderActionsParams {
  /** Merges a server-updated project into the list (from useDashboardProjects). */
  applyUpdatedProject: (project: ProjectSummary) => void;
  /** Re-fetches the project + folder lists (the provider's `load`, exposed as reload). */
  reload: () => Promise<void>;
  /** Normalizes a raw project payload (from useDashboardProjects). */
  normalizeProjectPayload: (project: any) => ProjectSummary;
  /** Closes the project context menu after a successful move (from useDashboardModals). */
  closeProjectContextMenu: () => void;
  /** The folder-list setter (the hook's useState setter from useDashboardProjects). */
  setProjectFolders: Dispatch<SetStateAction<ProjectFolder[]>>;
  /** `showToast` from `useToast()`. */
  showToast: ReturnType<typeof useToast>['showToast'];
  /** The `home.toast` translator. */
  tToast: ReturnType<typeof useTranslations>;
}

interface UseDashboardFolderActionsResult {
  handleMoveProjectToFolder: (project: ProjectSummary, folderId: string | null) => Promise<void>;
  applyCreatedFolder: (folder: ProjectFolder) => void;
  applyUpdatedFolder: (folder: ProjectFolder) => void;
  applyDeletedFolder: (folderId: string) => void;
}

/**
 * `useDashboardFolderActions` — the project↔folder mutations the dashboard
 * exposes: moving a project into / out of a folder (server call + reconcile),
 * and the local apply-helpers callers invoke after their own folder CRUD calls.
 *
 * Extracted verbatim from `DashboardContext` (Phase 1.6 (6)). The
 * `handleMoveProjectToFolder` body (the PATCH, the `extractApiErrorMessage`
 * throw on !ok / success:false, the updated-project reconcile vs `reload()`
 * fallback, the moved/removed toast, and the context-menu close) and the three
 * `setProjectFolders`-based apply helpers are byte-for-byte identical to the
 * originals — only the cross-cutting collaborators
 * (`applyUpdatedProject` / `reload` / `normalizeProjectPayload` /
 * `closeProjectContextMenu` / `setProjectFolders` / `showToast` / `tToast`) are
 * injected as params. The original's `load` is injected under the name `reload`.
 */
export function useDashboardFolderActions({
  applyUpdatedProject,
  reload,
  normalizeProjectPayload,
  closeProjectContextMenu,
  setProjectFolders,
  showToast,
  tToast,
}: UseDashboardFolderActionsParams): UseDashboardFolderActionsResult {
  const handleMoveProjectToFolder = useCallback(
    async (project: ProjectSummary, folderId: string | null) => {
      try {
        const response = await fetchWithAuth(`/api/projects/${project.id}/folder`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folderId }),
        });

        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.success === false) {
          throw new Error(extractApiErrorMessage(payload, 'Failed to update project folder'));
        }

        const updatedProject =
          payload?.data && typeof payload.data === 'object' ? normalizeProjectPayload(payload.data) : null;
        if (updatedProject) {
          applyUpdatedProject(updatedProject);
        } else {
          await reload();
        }

        showToast(folderId ? tToast('projectMovedToFolder') : tToast('projectRemovedFromFolder'), 'success');
        closeProjectContextMenu();
      } catch (error) {
        console.warn('Failed to move project to folder:', error);
        showToast(error instanceof Error ? error.message : tToast('failedMoveFolder'), 'error');
      }
    },
    [applyUpdatedProject, closeProjectContextMenu, reload, normalizeProjectPayload, showToast, tToast],
  );

  // Helpers to apply folder mutations locally (callers do the API call, then notify via these).
  const applyCreatedFolder = useCallback(
    (folder: ProjectFolder) => {
      setProjectFolders((prev) => [folder, ...prev]);
    },
    [setProjectFolders],
  );

  const applyUpdatedFolder = useCallback(
    (folder: ProjectFolder) => {
      setProjectFolders((prev) => prev.map((f) => (f.id === folder.id ? folder : f)));
    },
    [setProjectFolders],
  );

  const applyDeletedFolder = useCallback(
    (folderId: string) => {
      setProjectFolders((prev) => prev.filter((f) => f.id !== folderId));
    },
    [setProjectFolders],
  );

  return {
    handleMoveProjectToFolder,
    applyCreatedFolder,
    applyUpdatedFolder,
    applyDeletedFolder,
  };
}
